import { SORT_BY_MAC_FN, deepCopy, ppp, unique, SORT_BY_ID_FN, SORT_DEVICE_GENERAL_FN } from '@/utils'
import { IDevice, IDeviceAss, dev_identity_t, IVW } from '@/types/model'
import {api} from '@/singleton'
import { Commit } from 'vuex'
import { Extender, ApiGenericDeviceParam, ApiVw, OsdConfig, OsdImage, Matrix, Preset } from '@/types/api'
import i18n from '@/i18n'

export interface State {
  /** If action fetchList() has already finished after the last time browser refreshing */
  firstTimeFetched: boolean
  list: IDevice[]
  vwList: ApiVw[]
  osd: {
    list: OsdConfig[]
    imgList: OsdImage[]
  },
  presetList: Preset[]
}

interface Context {
  commit: Commit,
  state: State
}

// ======================================================
// Main
// ======================================================
const thisState: State = {
  firstTimeFetched: false,
  list: [],
  vwList: [],
  osd: {
    list: [],
    imgList: [],
  },
  presetList: []
}

export interface IAssignableDeviceList {
  querys: Extender[]
  snaps: Array<ApiGenericDeviceParam<string>>
}

export interface IAssignableVW {
  /** ExtenderVW.id  (IVW.id) */
  vwid: number
  value: Partial<IVW>
}

function createRxListBySize (vw: ApiVw) {
  vw.rxList = []
  const expected: number = vw.layout.rows * vw.layout.columns
  for (let i = 1; i <= expected; i++) {
    vw.rxList.push({
      displayIndex: i,
      mac: '',
      osd: 0,
      bezel: {
        ih: 0,
        iw: 0,
        oh: 0,
        ow: 0
      }
    })
  }
}

function updateDevice (target: IDevice, newVal: Extender) {
  Object.assign(target, newVal)
  target.online = newVal.connectionTime >= 0
}

function createDevice (qi: Extender): IDevice {
  return Object.assign(qi, {
    online: qi.connectionTime >= 0,
    rebooting: false,
    $meta: {
      blinkLedTimeoutId: -1
    }
  })
}

const mutations = {
  _firstTimeFetched (state: State, to: boolean = false): void {
    state.firstTimeFetched = to
  },
  assignList (state: State, list: Extender[]): void {
    list.sort(SORT_DEVICE_GENERAL_FN)
    state.list = list.map(ext => createDevice(ext))
    ppp(list)
  },
  assignListWithoutBreakReferences (state: State, nv: IAssignableDeviceList) {
    for (const q of nv.querys) {
      const target = state.list.find((x) => x.mac === q.mac)
      if (target) {
        updateDevice(target, q)
      } else {
        const snap = nv.snaps.find((s) => s.mac === q.mac)
        state.list.push(createDevice(q))
      }
    }
    state.list.sort(SORT_DEVICE_GENERAL_FN)
  },
  assignDev (state: State, newVal: IDeviceAss): void {
    const nv = Object.assign({}, newVal)
    const ref = getDevRef(nv.asstar)
    if (!ref) {return}
    delete nv.asstar
    Object.assign(ref, nv)
  },
  assignVw (state: State, nv: IAssignableVW) {
    const vw = state.vwList.find(x => nv.vwid === x.id)
    if (!vw) {
      console.error('[To Developer] This should not happened.')
      return
    }
    Object.assign(vw, nv.value)
  },
  assignVwList (state: State, nv: ApiVw[]) {
    state.vwList = nv.sort(SORT_BY_ID_FN)
  },
  addVw (state: State, nv: ApiVw) {
    createRxListBySize(nv)
    state.vwList.push(nv)
  },
  deleteVw (state: State, vwId: number) {
    const idx = state.vwList.findIndex(v => v.id === vwId)
    if (idx === -1) { throw new Error('[To Developer] This should not be happened.') }
    state.vwList.splice(idx, 1)
  },
  assignDevFromWs (state: State, newDev: Extender) {
    const existing = state.list.find((x) => x.mac === newDev.mac)
    if (existing) {
      updateDevice(existing, newDev)
    } else {
      state.list.push(createDevice(newDev))
    }
    // state.list.sort(SORT_BY_MAC_FN)
    state.list.sort(SORT_DEVICE_GENERAL_FN)
  },
  assignOsdList (state: State, osdList: OsdConfig[]) {
    state.osd.list = osdList
  },
  assignOrCreateOsd (state: State, osd: OsdConfig) {
    const match = state.osd.list.find(x => x.id === osd.id)
    if (!match) {
      state.osd.list.push(osd) // create
    } else {
      Object.assign(match, osd) // update
    }
  },
  deleteOsd (state: State, osd: OsdConfig) {
    const idx = state.osd.list.indexOf(osd)
    if (idx === -1) { console.error('[To Developer] Impossible'); return }
    state.osd.list.splice(idx, 1)
  },
  assignOsdImgList (state: State, osdImgList: OsdImage[]) {
    const time = Date.now()
    for (const img of osdImgList) {
      img.fileName = `/osd-images/${img.id}?nonsense=${time}`
    }
    state.osd.imgList = osdImgList
  },
  assignOrCreateOsdImg (state: State, osdImg: OsdImage) {
    const time = Date.now()
    osdImg.fileName = `/osd-images/${osdImg.id}?nonsense=${time}`
    const match = state.osd.imgList.find(x => x.id === osdImg.id)
    if (!match) {
      state.osd.imgList.push(osdImg) // create
    } else {
      Object.assign(match, osdImg) // update
    }
  },
  deleteOsdImg (state: State, osdImg: OsdImage) {
    const idx = state.osd.imgList.indexOf(osdImg)
    if (idx === -1) { console.error('[To Developer] Impossible'); return }
    state.osd.imgList.splice(idx, 1)
  },
  assignPresetList (state: State, list: Preset[]) {
    list.sort(SORT_BY_ID_FN)
    state.presetList = list
  },
  assignOrCreatePreset (state: State, preset: Preset) {
    const match = state.presetList.find(x => x.id === preset.id)
    if (!match) {
      state.presetList.push(preset) // create
    } else {
      Object.assign(match, preset) // update
    }
  },
  deletePreset (state: State, preset: Preset) {
    const idx = state.presetList.findIndex(x => x.id === preset.id)
    if (idx === -1) { console.error('[To Developer] Impossible'); return }
    state.presetList.splice(idx, 1)
  },
}

const actions = {
}

/** Device with mac has N correcponding IDevice mapped in array. */
export type MatrixMap = Map<string, IDevice[]>

const getters = {
  deviceList (state: State): IDevice[] {
    return state.list.map(x => Object.assign({}, x, {
      online: x.connectionTime > 0,
      $meta: {
        blinkLedTimeoutId: -1
      }
    }))
  },
  /** Get a Map {[mac: string]: IDevice[]} */
  matrixMap (state: State): MatrixMap {
    const fin: MatrixMap = new Map<string, IDevice[]>()
    for (const dev of state.list) {
      const oppositeType = dev.deviceType === 'TX' ? 'RX' : 'TX'
      const sameGroupDevs = state.list.filter((x) => x.groupID === dev.groupID)
      const oppositeDevs = sameGroupDevs.filter((x) => x.deviceType === oppositeType)
      if (oppositeDevs.length === 0) {
        fin.set(dev.mac, [])
      } else if (oppositeType === 'TX') {
        if (oppositeDevs.length > 1) { console.warn('[For Developer] Multiple TX has same groupID? You should validate to avoid such configuration.') }
        fin.set(dev.mac, oppositeDevs)
      } else {
        fin.set(dev.mac, oppositeDevs)
      }
    }
    // console.log('matrixMap', deepCopy(state.list), fin)
    return fin
  },
  matrixList (state: State): Matrix[] {
    return state.list
    .filter(x => x.deviceType === 'RX' && x.rxInfo.activeState.which === 'matrix')
    .map(rx => Object.assign(rx.rxInfo.activeState.matrix))
  },
  vwList (state: State): IVW[] {  // DEPRECATED: Useless
    return state.vwList.map((vw) => {
      return Object.assign({}, vw, {
        txMacList: vw.txList.map((m) => m.mac),
        rxMacList: vw.rxList.map((m) => m.mac),
        txAmount: vw.txList.filter(x => x.mac).length,
        rxAmount: vw.rxList.filter(x => x.mac).length
      })
    })
  }
}

export default {
  namespaced: true,
  state: thisState,
  mutations,
  actions,
  getters
}

// ======================================================
// Tools
// ======================================================
/** Get the reference to IDevice via IDevice or MAC */
export function getDevRef (x: dev_identity_t): IDevice | null {
  if (!x) { return null }
  if (typeof x === 'string') { return thisState.list.find((d) => d.mac === x) || null }
  if (thisState.list.includes(x)) { return x }
  return null
}

export function getDev (x: dev_identity_t): IDevice | null {
  if (typeof x === 'string') { return thisState.list.find((d) => d.mac === x) || null }
  return x
}

export function getDevMAC (x: dev_identity_t): string | null {
  if (!x) { return null }
  if (typeof x === 'string') {
    const m = thisState.list.find((d) => d.mac === x)
    return m ? m.mac : null
  }
  if (thisState.list.includes(x)) { return x.mac }
  return null
}

export function getDevName (x: dev_identity_t): string {
  const dev = getDev(x)
  if (!dev) { return `(${i18n.t('term.error')})` }
  if (!dev.mac) { return `(${i18n.t('term.untitled')}) ${dev.vendor}_?` }
  return dev.deviceName ? dev.deviceName : `(${i18n.t('term.untitled')}) ${dev.vendor}_${dev.mac.replace(/:/g, '').slice(8)}`
}
export function getUnoccupiedGroupId (): number {
  const occupied = thisState.list.map((x) => x.groupID)
  for (let i = 64; i < 1024; i++) {
    if (!occupied.includes(i)) {
      return i
    }
  }
  console.error('[What] Is this can be happened in real world? 1024 Extenders?!')
  return -1
}

// export function distributeGroupId ():
